/**
 * Admin Login Page - ZO Bull Algo Trading Platform
 * Version: 6.2.0
 * 
 * Purpose: Dedicated admin authentication page for platform administrators
 */
import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import './Login.css'

interface AdminLoginResponse {
  access_token: string
  token_type: string
  expires_in: number
}

function AdminLogin() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Check if already logged in as admin
  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (adminToken) {
      navigate('/admin/dashboard')
    }
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: email,
          password: password,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed')
      }

      // Store admin token
      localStorage.setItem('admin_token', data.access_token)
      localStorage.setItem('admin_email', email)
      localStorage.setItem('admin_login_time', new Date().toISOString())

      // Redirect to admin dashboard
      navigate('/admin/dashboard')
    } catch (err: any) {
      console.error('Admin login error:', err)
      setError(err.message || 'Login failed. Please check your credentials.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page admin-login-page">
      <div className="login-container admin-login-container">
        <div className="login-header admin-header">
          <div className="logo-section">
            <span className="logo-icon">🐂</span>
            <h1>ZO Bull Admin</h1>
          </div>
          <p className="tagline">Admin Dashboard - Platform Management</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="email">Admin Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              disabled={loading}
              required
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                disabled={loading}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In to Admin'}
          </button>

          <div className="form-divider">
            <span>🔒 Secure Admin Access</span>
          </div>
        </form>

        <div className="login-footer">
          <div className="footer-links">
            <Link to="/landing">← Back to Home</Link>
            <span className="dot">•</span>
            <Link to="/login">User Login</Link>
          </div>
          <p>ZO Bull Admin v6.2.0 | © 2024-2026</p>
        </div>
      </div>

      <style>{`
        .admin-login-container {
          max-width: 480px;
          border-top: 4px solid #dc2626;
        }
        
        .admin-header {
          background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
        }
        
        .admin-login-container .login-btn {
          background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
        }
        
        .admin-login-container .login-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%);
        }
        
        .password-input-wrapper {
          position: relative;
          display: flex;
        }
        
        .password-input-wrapper input {
          flex: 1;
          padding-right: 45px;
        }
        
        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 4px;
        }
        
        .admin-login-page::before {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(153, 27, 27, 0.1) 100%);
          pointer-events: none;
        }
      `}</style>
    </div>
  )
}

export default AdminLogin